import { Injectable, TemplateRef } from "@angular/core";
import { Router } from "@angular/router";
import { BtnActionEnum, Menu, MenuItemType } from "@evc/web-components";
import { TranslateService } from "@ngx-translate/core";
import { ASSET_TYPE_ROUTE, LIVE_ASSET_ROUTE } from "@rvi/asset-modules";
import { BehaviorSubject } from "rxjs";
import { ConfigurationItem } from "../../../_objects/tenant-config";
import { NetworkStatusService } from "../../../_services/network-status.service";
import { parseUrlToRoutePage, RoutePage, RouteSettings } from "../../../app-routes.enum";

type MenuItemExtended = MenuItemType & { isVisible: boolean };

@Injectable({
  providedIn: "root"
})
export class NavigationMenuService {
  constructor(
    private router: Router,
    private translate: TranslateService,
    private networkStatusService: NetworkStatusService
  ) {}

  private leftBarState = new BehaviorSubject<boolean>(true);

  public leftBarState$ = this.leftBarState.asObservable();
  public cta = new BehaviorSubject<TemplateRef<any> | null>(null);

  public toggleLeftBar(isLeftbarOpen: boolean): void {
    this.leftBarState.next(isLeftbarOpen);
  }

  private getDynamicLabelFor(key: string, tenantConfigs: ConfigurationItem[]) {
    return tenantConfigs?.find((config) => config.key == key)?.value?.toString() ?? "";
  }

  public buildLeftMenu = (
    isOnline: boolean,
    tenantConfig: ConfigurationItem[],
    assetModuleFeatureFlagEnabled: boolean,
    shouldShowSettingsMenu: boolean,
    url: string
  ): Menu[] => {
    const route = parseUrlToRoutePage(url);
    const isHiddenDefaultNavBar = route == RoutePage.InspectionDataManagement || route == RoutePage.InspectionReport;
    if (isHiddenDefaultNavBar) {
      return undefined;
    }
    const allMenuItems: MenuItemExtended[] = [
      {
        key: "inspections",
        text: this.translate.stream("topMenuBar.inspections"),
        isVisible: true,
        selected: url.includes(RoutePage.InspectionList),
        icon: "visibility-show",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.IndexPage])
        }
      },
      {
        key: "reports",
        text: this.translate.stream("topMenuBar.reports"),
        isVisible: true,
        selected: url.includes(`/${RoutePage.InspectionReportList}`),
        icon: "report",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.InspectionReportList])
        }
      },
      {
        key: "analytics",
        text: this.translate.stream("topMenuBar.analytics"),
        isVisible: isOnline,
        selected: url.includes(`/${RoutePage.AnalyticsPowerBiReport}`),
        icon: "circle-three-quarters",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.AnalyticsPowerBiReport])
        }
      },
      {
        key: "asset-types",
        text: this.translate.stream("topMenuBar.asset-types"),
        isVisible: assetModuleFeatureFlagEnabled,
        selected: url.includes(`/${ASSET_TYPE_ROUTE}`),
        icon: "blueprint",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: async () => {
            this.router.navigate([ASSET_TYPE_ROUTE]);
          }
        }
      },
      {
        key: "live-assets",
        text: this.translate.stream("topMenuBar.live-assets"),
        isVisible: assetModuleFeatureFlagEnabled,
        selected: url.includes(`/${LIVE_ASSET_ROUTE}`),
        icon: "live",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: async () => {
            this.router.navigate([LIVE_ASSET_ROUTE]);
          }
        }
      },
      {
        key: "tenant-config",
        text: this.translate.stream("settingTenant.configuration"),
        isVisible: shouldShowSettingsMenu,
        selected: url.includes(`/${RouteSettings.TenantConfig}`),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.TenantConfig])
        }
      },
      {
        key: "marks",
        text: this.getDynamicLabelFor("LabelOfMark", tenantConfig),
        isVisible: shouldShowSettingsMenu,
        selected: url.includes(`/${RouteSettings.Mark}`),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.Mark])
        }
      },
      {
        key: "titles",
        text: this.getDynamicLabelFor("LabelOfTitle", tenantConfig),
        isVisible: shouldShowSettingsMenu,
        selected: url.includes(`/${RouteSettings.Title}`),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.Title])
        }
      },
      {
        key: "tags",
        text: this.getDynamicLabelFor("LabelOfTags", tenantConfig),
        isVisible: shouldShowSettingsMenu && (!this.networkStatusService.isOnPremise || this.networkStatusService.isBrianMode),
        selected: url.includes(`/${RouteSettings.Tags}`),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.Tags])
        }
      }
    ];

    const items = allMenuItems.filter((item) => item.isVisible);

    return [{ items }];
  };
}
